"use client"

import { Filter, X, Users, MapPin, Trophy, Target } from "lucide-react"
import { useTranslations } from "next-intl"
import { useState, useCallback, useMemo, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { ExtendedCombobox } from "./extended-combobox"
import { cn } from "@/lib/utils"

export type FilterConfig = {
  status?: boolean
  gender?: boolean
  phase?: boolean
  event?: boolean
  unit?: boolean
  organisation?: boolean
}

export type FilterValues = {
  status: string | undefined
  gender: string | undefined
  phase: string | undefined
  event: string | undefined
  unit: string | undefined
  organisation: string | undefined
}

export type FilterOptions = {
  status?: Array<{ value: string, label: string }>
  gender?: Array<{ value: string, label: string }>
  phase?: Array<{ value: string, label: string }>
  event?: Array<{ value: string, label: string }>
  unit?: Array<{ value: string, label: string }>
  organisation?: Array<{ value: string, label: string }>
}

type OptimizedFiltersProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
  config: FilterConfig
  values: FilterValues
  options: FilterOptions
  onValuesChange: (values: FilterValues) => void
  onApplyFilters: () => void
  onClearFilters: () => void
}

// Componente para filtros de tipo badge (status, gender)
function BadgeFilter({
  label,
  icon: Icon,
  options,
  value,
  onChange,
  className
}: {
  label: string
  icon: React.ComponentType<{ className?: string }>
  options: Array<{ value: string, label: string }>
  value: string | undefined
  onChange: (value: string | undefined) => void
  className?: string
}) {
  return (
    <div className={cn("space-y-3", className)}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <Label className="text-sm font-medium text-foreground">{label}</Label>
      </div>
      <div className="flex flex-wrap gap-2">
        {options.map(option => (
          <Badge
            key={option.value}
            className={cn(
              "cursor-pointer transition-all duration-200 hover:scale-105",
              value === option.value
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-secondary text-secondary-foreground border-border hover:bg-accent hover:text-accent-foreground"
            )}
            onClick={() => onChange(value === option.value ? undefined : option.value)}
            variant="outline"
          >
            {option.label}
          </Badge>
        ))}
      </div>
    </div>
  )
}

// Componente para filtros de tipo combobox
function ComboboxFilter({
  label,
  icon: Icon,
  options,
  value,
  onChange,
  className
}: {
  label: string
  icon: React.ComponentType<{ className?: string }>
  options: Array<{ value: string, label: string }>
  value: string | undefined
  onChange: (value: string | undefined) => void
  className?: string
}) {
  return (
    <div className={cn("space-y-2", className)}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <Label className="text-sm font-medium text-foreground">{label}</Label>
      </div>
      <ExtendedCombobox
        items={options}
        onChange={value => onChange(value?.toString())}
        placeholder={`Seleccionar ${label.toLowerCase()}`}
        value={value}
      />
    </div>
  )
}


export function OptimizedFilters({
  open,
  onOpenChange,
  config,
  values,
  options,
  onValuesChange,
  onApplyFilters,
  onClearFilters,
}: OptimizedFiltersProps) {
  const t = useTranslations("floating_units")
  const [localValues, setLocalValues] = useState<FilterValues>(values)

  // Memoizar el conteo de filtros activos
  const activeFiltersCount = useMemo(() => {
    return Object.values(localValues).filter(value => value !== undefined).length
  }, [localValues])

  const handleValueChange = useCallback((key: keyof FilterValues, value: string | undefined) => {
    setLocalValues(prev => ({ ...prev, [key]: value }))
  }, [])

  const handleApply = useCallback(() => {
    onValuesChange(localValues)
    onApplyFilters()
  }, [localValues, onValuesChange, onApplyFilters])

  const handleClear = useCallback(() => {
    const clearedValues: FilterValues = {
      status: undefined,
      gender: undefined,
      phase: undefined,
      event: undefined,
      unit: undefined,
      organisation: undefined,
    }
    setLocalValues(clearedValues)
    onValuesChange(clearedValues)
    onClearFilters()
  }, [onValuesChange, onClearFilters])

  // Actualizar valores locales cuando cambien los valores externos
  useEffect(() => {
    setLocalValues(values)
  }, [values])

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader className="border-b border-border pb-4">
          <DialogTitle className="flex items-center gap-3 text-xl font-semibold text-foreground">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
              <Filter className="h-4 w-4 text-primary-foreground" />
            </div>
            <div className="flex flex-col">
              <span>{t("filter_modal.advanced_filters")}</span>
              {activeFiltersCount > 0 && (
                <span className="text-sm font-normal text-muted-foreground">
                  {activeFiltersCount} filtro{activeFiltersCount !== 1 ? 's' : ''} activo{activeFiltersCount !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto py-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Columna izquierda */}
            <div className="space-y-6">
              {config.status && options.status && (
                <BadgeFilter
                  icon={Target}
                  label={t("filter_modal.status_label")}
                  options={options.status}
                  value={localValues.status}
                  onChange={(value) => handleValueChange("status", value)}
                />
              )}

              {config.gender && options.gender && (
                <BadgeFilter
                  icon={Users}
                  label={t("filter_modal.gender_label")}
                  options={options.gender}
                  value={localValues.gender}
                  onChange={(value) => handleValueChange("gender", value)}
                />
              )}

              {config.phase && options.phase && (
                <ComboboxFilter
                  icon={MapPin}
                  label={t("filter_modal.phase_label")}
                  options={options.phase}
                  value={localValues.phase}
                  onChange={(value) => handleValueChange("phase", value)}
                />
              )}

              {config.organisation && options.organisation && (
                <ComboboxFilter
                  icon={Users}
                  label={t("filter_modal.organisation_label")}
                  options={options.organisation}
                  value={localValues.organisation}
                  onChange={(value) => handleValueChange("organisation", value)}
                />
              )}
            </div>

            {/* Columna derecha */}
            <div className="space-y-6">
              {config.event && options.event && (
                <ComboboxFilter
                  icon={Trophy}
                  label={t("filter_modal.event_label")}
                  options={options.event}
                  value={localValues.event}
                  onChange={(value) => handleValueChange("event", value)}
                />
              )}

              {config.unit && options.unit && (
                <ComboboxFilter
                  icon={Target}
                  label={t("filter_modal.unit_label")}
                  options={options.unit}
                  value={localValues.unit}
                  onChange={(value) => handleValueChange("unit", value)}
                />
              )}

            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-3 border-t border-border pt-4">
          <Button
            className="flex items-center gap-2 hover:bg-destructive hover:text-destructive-foreground"
            onClick={handleClear}
            variant="outline"
          >
            <X className="h-4 w-4" />
            {t("filter_modal.clear_filters")}
          </Button>
          <Button 
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-primary-foreground"
            onClick={handleApply}
          >
            <Filter className="h-4 w-4" />
            {t("filter_modal.apply_filters")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
